import React, { useEffect, useRef, useState } from 'react';
import CodeMirror from 'codemirror';
import 'codemirror/mode/jsx/jsx';
import 'codemirror/mode/javascript/javascript';
import 'codemirror/mode/xml/xml';
import 'codemirror/addon/edit/closebrackets';
import 'codemirror/addon/edit/matchbrackets';
import 'codemirror/addon/display/placeholder';
import 'codemirror/lib/codemirror.css';
import 'codemirror/theme/material.css';

type CodeEditorProps = {
  // ID of a server-rendered <textarea> that holds the form value. We decorate
  // this textarea with CodeMirror (via fromTextArea), hiding it and syncing
  // CM content back to it on every change so the form continues to submit a
  // real form field. Keeping the textarea server-rendered means non-JS
  // clients (Capybara/Rack-test feature specs, anything that doesn't run
  // our bundle) can still read and write the same field.
  textareaId: string;
  collapsedHeight?: number;
  label?: string;
};

const CodeEditor = ({
  textareaId,
  collapsedHeight = 280,
  label,
}: CodeEditorProps) => {
  const editorRef = useRef<CodeMirror.EditorFromTextArea | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [lineCount, setLineCount] = useState(0);
  const [mounted, setMounted] = useState(false);

  // Mount CodeMirror on the server-rendered textarea.
  useEffect(() => {
    if (editorRef.current) return;

    const textarea = document.getElementById(textareaId) as HTMLTextAreaElement | null;
    if (!textarea) {
      // eslint-disable-next-line no-console
      console.warn(`[CodeEditor] No textarea found with id="${textareaId}"`);
      return;
    }

    const cm = CodeMirror.fromTextArea(textarea, {
      mode: 'jsx',
      theme: 'material',
      lineNumbers: true,
      tabSize: 2,
      indentUnit: 2,
      indentWithTabs: false,
      autoCloseBrackets: true,
      matchBrackets: true,
      lineWrapping: false,
      placeholder: textarea.getAttribute('placeholder') || '',
      viewportMargin: Infinity,
    });

    // Sync CM content back into the textarea so the form submits the latest
    // code on every change.
    const sync = () => {
      cm.save();
      setLineCount(cm.lineCount());
    };
    cm.on('changes', sync);
    sync();

    editorRef.current = cm;
    setMounted(true);

    // Move CM's wrapper into our React-managed container so our toolbar and
    // fullscreen styling apply to it.
    if (wrapperRef.current) {
      wrapperRef.current.appendChild(cm.getWrapperElement());
    }

    // CM measures layout at construction; if the host is hidden (Bootstrap
    // tab-pane that isn't active yet, or one mid-fade) everything ends up at
    // 0 and lines don't render until the user focuses in. Schedule staggered
    // refreshes to catch the layout once it settles, and listen for Bootstrap
    // tab activations to refresh on tab-switch as well.
    const refresh = () => cm.refresh();
    const refreshTimers = [0, 100, 300, 750].map((d) => window.setTimeout(refresh, d));

    const tabPane = wrapperRef.current?.closest('.tab-pane');
    let tabTrigger: HTMLElement | null = null;
    const onTabShown = () => refresh();
    if (tabPane && tabPane.id) {
      tabTrigger = document.querySelector(
        `[href="#${tabPane.id}"], [data-target="#${tabPane.id}"]`
      );
      tabTrigger?.addEventListener('shown.bs.tab', onTabShown);
    }

    return () => {
      refreshTimers.forEach((t) => window.clearTimeout(t));
      tabTrigger?.removeEventListener('shown.bs.tab', onTabShown);
      cm.off('changes', sync);
      cm.toTextArea();
      editorRef.current = null;
      setMounted(false);
    };
  }, [textareaId]);

  // Refresh layout when entering/leaving fullscreen.
  useEffect(() => {
    if (editorRef.current) editorRef.current.refresh();
  }, [expanded]);

  // Escape closes fullscreen.
  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setExpanded(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expanded]);

  return (
    <div
      className={`code-editor ${expanded ? 'code-editor--expanded' : ''}`}
      style={{
        position: expanded ? 'fixed' : 'relative',
        inset: expanded ? 0 : 'auto',
        zIndex: expanded ? 9999 : 'auto',
        background: expanded ? '#263238' : 'transparent',
        display: mounted ? 'flex' : 'none',
        flexDirection: 'column',
      }}
    >
      <div
        className="code-editor__toolbar"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '6px 10px',
          background: '#37474f',
          color: '#cfd8dc',
          fontSize: '12px',
          borderTopLeftRadius: expanded ? 0 : '4px',
          borderTopRightRadius: expanded ? 0 : '4px',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {label && <strong style={{ color: '#fff' }}>{label}</strong>}
          <span style={{ opacity: 0.7 }}>JSX</span>
          <span style={{ opacity: 0.7 }}>· {lineCount} {lineCount === 1 ? 'line' : 'lines'}</span>
        </span>
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          title={expanded ? 'Collapse (Esc)' : 'Expand to fullscreen'}
          style={{
            background: 'transparent',
            color: '#cfd8dc',
            border: '1px solid rgba(207, 216, 220, 0.4)',
            borderRadius: '3px',
            padding: '2px 10px',
            fontSize: '12px',
            cursor: 'pointer',
          }}
        >
          {expanded ? '⤡ Collapse' : '⤢ Expand'}
        </button>
      </div>

      <div
        ref={wrapperRef}
        className="code-editor__body"
        style={{
          flex: expanded ? '1 1 auto' : '0 0 auto',
          height: expanded ? 'auto' : `${collapsedHeight}px`,
          overflow: 'hidden',
          border: '1px solid #37474f',
          borderTop: 'none',
          borderBottomLeftRadius: expanded ? 0 : '4px',
          borderBottomRightRadius: expanded ? 0 : '4px',
        }}
      />

      <style>{`
        .code-editor .CodeMirror {
          height: 100% !important;
          font-family: 'SF Mono', 'Monaco', 'Menlo', 'Consolas', 'Liberation Mono', monospace;
          font-size: 13px;
          line-height: 1.5;
        }
        .code-editor--expanded .CodeMirror {
          height: calc(100vh - 34px) !important;
        }
        .code-editor button:hover {
          background: rgba(207, 216, 220, 0.1) !important;
        }
        .code-editor .CodeMirror-placeholder {
          color: #78909c !important;
          opacity: 0.7;
        }
      `}</style>
    </div>
  );
};

export default CodeEditor;
